import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail, signInWithPopup } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db, googleProvider, githubProvider } from '../firebase';
import { Link } from 'react-router-dom';

const TICKER_ITEMS = [
  { name: 'Wireless Headphones', qty: 142, status: 'OK' },
  { name: 'USB-C Hub', qty: 8, status: 'LOW' },
  { name: 'Mechanical Keyboard', qty: 57, status: 'OK' },
  { name: 'Monitor Stand', qty: 0, status: 'OUT' },
  { name: 'Laptop Sleeve 13"', qty: 234, status: 'OK' },
  { name: 'HDMI Cable 2m', qty: 19, status: 'OK' },
  { name: 'Webcam 1080p', qty: 3, status: 'LOW' },
  { name: 'Desk Lamp LED', qty: 88, status: 'OK' },
  { name: 'Mouse Pad XL', qty: 0, status: 'OUT' },
  { name: 'Ergonomic Chair', qty: 12, status: 'OK' },
  { name: 'Cable Organiser', qty: 67, status: 'OK' },
  { name: 'Power Strip 6-port', qty: 5, status: 'LOW' },
  { name: 'Standing Desk Mat', qty: 41, status: 'OK' },
  { name: 'Wireless Charger', qty: 29, status: 'OK' },
  { name: 'Laptop Stand', qty: 1, status: 'LOW' },
  { name: 'Blue Light Glasses', qty: 76, status: 'OK' },
  { name: 'Thermal Paste 4g', qty: 0, status: 'OUT' },
  { name: 'M.2 SSD 1TB', qty: 18, status: 'OK' },
  { name: 'Noise Cancelling Buds', qty: 44, status: 'OK' },
  { name: 'USB Microphone', qty: 6, status: 'LOW' },
  { name: 'Green Screen 150cm', qty: 9, status: 'OK' },
  { name: 'Stream Deck Mini', qty: 0, status: 'OUT' },
];
const STATUS_COLOR = { OK: '#34d399', LOW: '#fbbf24', OUT: '#f87171' };

function useIsMobile() {
  const [v, setV] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const h = () => setV(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return v;
}

function TickerStrip() {
  const rows = [...TICKER_ITEMS, ...TICKER_ITEMS, ...TICKER_ITEMS];
  return (
    <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none', userSelect: 'none' }}>
      <style>{`
        @keyframes tickerScroll{0%{transform:translateY(0)}100%{transform:translateY(-33.333%)}}
        .ticker-inner{animation:tickerScroll 44s linear infinite;}
        @media(prefers-reduced-motion:reduce){.ticker-inner{animation:none}}
      `}</style>
      <div className="ticker-inner">
        {rows.map((item, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 24px', borderBottom: '1px solid rgba(255,255,255,0.03)', opacity: 0.1 }}>
            <span style={{ width: '34px', textAlign: 'right', fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', fontWeight: 600, color: STATUS_COLOR[item.status], flexShrink: 0 }}>{String(item.qty).padStart(3, '0')}</span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '10px', color: '#bfdbfe', flex: 1 }}>{item.name}</span>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '8px', fontWeight: 700, color: STATUS_COLOR[item.status], border: `1px solid ${STATUS_COLOR[item.status]}`, padding: '1px 4px', borderRadius: '3px', flexShrink: 0 }}>{item.status}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FloatInput({ id, label, type, value, onChange, autoComplete, rightSlot }) {
  const [focused, setFocused] = useState(false);
  const lifted = focused || value.length > 0;
  return (
    <div style={{ position: 'relative', marginBottom: '14px' }}>
      <label htmlFor={id} style={{
        position: 'absolute', left: '14px',
        top: lifted ? '8px' : '50%', transform: lifted ? 'none' : 'translateY(-50%)',
        fontSize: lifted ? '10px' : '15px', color: focused ? '#3b82f6' : '#94a3b8',
        fontWeight: 500, letterSpacing: lifted ? '0.05em' : '0',
        textTransform: lifted ? 'uppercase' : 'none',
        transition: 'all 0.18s', pointerEvents: 'none', zIndex: 1,
      }}>{label}</label>
      <input
        id={id} type={type} value={value} onChange={onChange}
        autoComplete={autoComplete} required
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: lifted ? '24px 14px 8px' : '18px 14px',
          border: `1.5px solid ${focused ? '#3b82f6' : '#e2e8f0'}`,
          borderRadius: '12px', fontSize: '16px', color: '#1e293b',
          backgroundColor: focused ? '#ffffff' : '#f8fafc', outline: 'none',
          transition: 'border-color 0.18s, box-shadow 0.18s, background-color 0.18s',
          boxShadow: focused ? '0 0 0 3px rgba(59,130,246,0.12)' : 'none',
          WebkitAppearance: 'none',
        }}
      />
      {rightSlot && <div style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)' }}>{rightSlot}</div>}
    </div>
  );
}

function OAuthBtn({ icon, label, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <button type="button" onClick={onClick}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        padding: '13px 14px', border: `1.5px solid ${hover ? '#cbd5e1' : '#e2e8f0'}`,
        borderRadius: '12px', background: hover ? '#f1f5f9' : '#ffffff',
        fontSize: '14px', fontWeight: 600, color: '#374151',
        cursor: 'pointer', transition: 'all 0.15s', WebkitAppearance: 'none', minHeight: '48px',
      }}>
      <span style={{ fontSize: '17px' }}>{icon}</span>{label}
    </button>
  );
}

async function createUserDocIfNew(user) {
  const ref = doc(db, 'users', user.uid);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, { email: user.email, name: user.displayName || '', role: 'staff', orgId: null, createdAt: new Date() });
  }
}

// Human-readable messages for Firebase auth error codes
function oauthErrorMessage(code, provider) {
  switch (code) {
    case 'auth/popup-blocked':
      return 'Popup was blocked by your browser. Please allow popups for this site and try again.';
    case 'auth/popup-closed-by-user':
      return null; // silent — user dismissed intentionally
    case 'auth/cancelled-popup-request':
      return null; // silent — another popup was already opening
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.';
    case 'auth/network-request-failed':
      return 'Network error. Check your connection and try again.';
    default:
      return `${provider} sign-in failed. Please try again.`;
  }
}

export default function Login() {
  const isMobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);

  // ── Separated error states so providers can't bleed into each other ──
  const [emailError, setEmailError]   = useState(''); // email/password form errors
  const [oauthError, setOauthError]   = useState(''); // OAuth button errors

  const [loading, setLoading]         = useState(false);
  const [oauthLoading, setOauthLoading] = useState(''); // 'google' | 'github' | ''
  const [showReset, setShowReset]     = useState(false);
  const [resetEmail, setResetEmail]   = useState('');
  const [resetSent, setResetSent]     = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError]   = useState('');
  const [btnHover, setBtnHover]       = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setEmailError('');
    setOauthError(''); // clear any stale OAuth error when user submits the form
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch {
      setEmailError('Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault(); setResetError('');
    if (!resetEmail.trim()) { setResetError('Please enter your email address.'); return; }
    setResetLoading(true);
    try { await sendPasswordResetEmail(auth, resetEmail.trim()); setResetSent(true); }
    catch (err) { setResetError(err.code === 'auth/user-not-found' ? 'No account found with this email.' : 'Failed to send reset email.'); }
    finally { setResetLoading(false); }
  };

  const handleGoogle = async () => {
    // Clear only OAuth error — don't touch emailError
    setOauthError('');
    setOauthLoading('google');
    try {
      const r = await signInWithPopup(auth, googleProvider);
      await createUserDocIfNew(r.user);
    } catch (err) {
      const msg = oauthErrorMessage(err.code, 'Google');
      if (msg) setOauthError(msg);
    } finally {
      setOauthLoading('');
    }
  };

  const handleGithub = async () => {
    // Clear only OAuth error — don't touch emailError
    setOauthError('');
    setOauthLoading('github');
    try {
      const r = await signInWithPopup(auth, githubProvider);
      await createUserDocIfNew(r.user);
    } catch (err) {
      const msg = oauthErrorMessage(err.code, 'GitHub');
      if (msg) setOauthError(msg);
    } finally {
      setOauthLoading('');
    }
  };

  const primaryBtn = (disabled) => ({
    width: '100%', padding: '16px',
    background: disabled ? '#93c5fd' : btnHover
      ? 'linear-gradient(135deg,#1d4ed8,#2563eb)'
      : 'linear-gradient(135deg,#2563eb,#3b82f6)',
    color: '#fff', border: 'none', borderRadius: '12px',
    fontSize: '16px', fontWeight: 700, fontFamily: "'Syne',sans-serif",
    cursor: disabled ? 'not-allowed' : 'pointer', letterSpacing: '-0.01em',
    boxShadow: disabled ? 'none' : '0 4px 16px rgba(59,130,246,0.38)',
    transition: 'all 0.18s', transform: btnHover && !disabled ? 'translateY(-1px)' : 'none',
    marginTop: '6px', minHeight: '52px', WebkitAppearance: 'none',
  });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@600;700;800&family=JetBrains+Mono:wght@400;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { -webkit-text-size-adjust: 100%; overflow-x: hidden; }
        @keyframes floatA{0%,100%{transform:translateY(0) rotate(-4deg)}50%{transform:translateY(-14px) rotate(-4deg)}}
        @keyframes floatB{0%,100%{transform:translateY(0) rotate(3deg)}50%{transform:translateY(-12px) rotate(3deg)}}
        @keyframes floatC{0%,100%{transform:translateY(0)}50%{transform:translateY(-10px)}}
        @keyframes floatD{0%,100%{transform:translateY(0) rotate(-2deg)}50%{transform:translateY(-8px) rotate(-2deg)}}
      `}</style>

      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        minHeight: '100vh',
        minHeight: '-webkit-fill-available',
      }}>

        {/* ══ LEFT / TOP BRANDING PANEL ══ */}
        <div style={{
          flex: isMobile ? 'none' : 1,
          position: 'relative',
          background: 'linear-gradient(135deg, #020817 0%, #0c1a3a 25%, #111827 45%, #1a1040 65%, #0f2d5a 85%, #082f4a 100%)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: isMobile ? '40px 24px 48px' : '0 56px',
          overflow: 'hidden',
          minHeight: isMobile ? 'auto' : '100vh',
        }}>
          {!isMobile && <TickerStrip />}

          <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: '-10%', right: '-8%', width: '450px', height: '450px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.26) 0%, transparent 68%)' }} />
            <div style={{ position: 'absolute', bottom: '-12%', left: '-8%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(16,185,129,0.18) 0%, transparent 65%)' }} />
            <div style={{ position: 'absolute', top: '42%', left: '-5%', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(245,158,11,0.10) 0%, transparent 65%)' }} />
            <div style={{ position: 'absolute', top: '18%', left: '32%', width: '320px', height: '320px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 65%)' }} />
            <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 55% 60% at 46% 50%, transparent 15%, rgba(2,8,23,0.80) 100%)' }} />
          </div>

          {!isMobile && (<>
            <div style={{ position: 'absolute', top: '8%', right: '5%', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(14px)', border: '1px solid rgba(252,165,165,0.7)', borderRadius: '16px', padding: '14px 18px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)', animation: 'floatA 5s ease-in-out infinite', minWidth: '200px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>🚨</div>
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#dc2626', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Low Stock Alert</span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>USB-C Hub</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Only 8 units left</span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#ef4444', background: '#fef2f2', padding: '2px 8px', borderRadius: '6px' }}>Min: 20</span>
              </div>
            </div>

            <div style={{ position: 'absolute', top: '34%', right: '3%', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(14px)', border: '1px solid rgba(134,239,172,0.7)', borderRadius: '16px', padding: '14px 18px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)', animation: 'floatB 6s ease-in-out infinite', minWidth: '210px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>✅</div>
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#059669', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sale Logged</span>
              </div>
              <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '4px' }}>Mechanical Keyboard × 5</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Stock updated</span>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#059669', background: '#f0fdf4', padding: '2px 8px', borderRadius: '6px' }}>52 left</span>
              </div>
            </div>

            <div style={{ position: 'absolute', bottom: '10%', right: '5%', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(14px)', border: '1px solid rgba(165,180,252,0.7)', borderRadius: '16px', padding: '14px 18px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)', animation: 'floatC 7s ease-in-out infinite', minWidth: '196px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>📈</div>
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#6366f1', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Sales Velocity</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '5px', height: '38px', marginBottom: '8px' }}>
                {[35, 55, 42, 72, 50, 85, 68].map((h, i) => (
                  <div key={i} style={{ flex: 1, height: `${h}%`, background: i === 6 ? '#6366f1' : `rgba(99,102,241,${0.25 + i * 0.1})`, borderRadius: '3px 3px 0 0' }} />
                ))}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                <span style={{ fontWeight: 700, color: '#6366f1' }}>+18%</span> vs last week
              </div>
            </div>

            <div style={{ position: 'absolute', top: '60%', left: '2%', background: 'rgba(255,255,255,0.96)', backdropFilter: 'blur(14px)', border: '1px solid rgba(253,211,77,0.6)', borderRadius: '16px', padding: '14px 18px', boxShadow: '0 12px 40px rgba(0,0,0,0.35)', animation: 'floatD 8s ease-in-out infinite 1s', minWidth: '186px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: '#fffbeb', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px' }}>👥</div>
                <span style={{ fontSize: '10px', fontWeight: 800, color: '#d97706', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Team Active</span>
              </div>
              <div style={{ display: 'flex', marginBottom: '6px' }}>
                {['KS', 'RM', 'SA', 'DP'].map((init, i) => (
                  <div key={i} style={{ width: '28px', height: '28px', borderRadius: '50%', background: ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b'][i], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', fontWeight: 800, color: '#fff', border: '2px solid #fff', marginLeft: i > 0 ? '-7px' : '0' }}>{init}</div>
                ))}
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>4 members online now</div>
            </div>
          </>)}

          <div style={{ position: 'relative', zIndex: 2, maxWidth: isMobile ? '100%' : '520px', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: isMobile ? '20px' : '32px' }}>
              <div style={{ width: isMobile ? '38px' : '44px', height: isMobile ? '38px' : '44px', borderRadius: '12px', background: 'linear-gradient(135deg,#3b82f6,#1d4ed8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '18px' : '22px', boxShadow: '0 4px 20px rgba(59,130,246,0.55)', flexShrink: 0 }}>📦</div>
              <span style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? '20px' : '24px', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em' }}>StockSpace</span>
            </div>

            {isMobile ? (
              <div>
                <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '36px', fontWeight: 800, color: '#fff', lineHeight: 1.12, letterSpacing: '-0.03em', marginBottom: '12px' }}>
                  Every item.<br />Every moment.<br />
                  <span style={{ background: 'linear-gradient(90deg,#60a5fa,#a78bfa,#34d399)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>In sync.</span>
                </h1>
                <p style={{ fontSize: '15px', color: '#94a3b8', lineHeight: 1.65, marginBottom: '20px' }}>
                  Real-time inventory, smart analytics, and team collaboration — free forever.
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                  {[
                    { icon: '⚡', text: 'Real-time sync', color: '#fbbf24', bg: 'rgba(251,191,36,0.14)', border: 'rgba(251,191,36,0.3)' },
                    { icon: '📊', text: 'Smart analytics', color: '#a78bfa', bg: 'rgba(167,139,250,0.14)', border: 'rgba(167,139,250,0.3)' },
                    { icon: '👥', text: 'Team roles', color: '#34d399', bg: 'rgba(52,211,153,0.14)', border: 'rgba(52,211,153,0.3)' },
                    { icon: '🔒', text: 'Free forever', color: '#60a5fa', bg: 'rgba(96,165,250,0.14)', border: 'rgba(96,165,250,0.3)' },
                  ].map(({ icon, text, color, bg, border }) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 12px', background: bg, border: `1px solid ${border}`, borderRadius: '20px' }}>
                      <span style={{ fontSize: '13px' }}>{icon}</span>
                      <span style={{ fontSize: '12px', fontWeight: 600, color }}>{text}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '4px', WebkitOverflowScrolling: 'touch' }}>
                  {[
                    { val: '2.4k+', label: 'Items', color: '#60a5fa' },
                    { val: '99.9%', label: 'Uptime', color: '#34d399' },
                    { val: '< 1s', label: 'Sync', color: '#a78bfa' },
                    { val: '100%', label: 'Free', color: '#fbbf24' },
                  ].map(({ val, label, color }) => (
                    <div key={label} style={{ flexShrink: 0, padding: '10px 14px', textAlign: 'center', background: 'rgba(255,255,255,0.06)', border: `1px solid ${color}30`, borderRadius: '12px' }}>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '16px', fontWeight: 700, color }}>{val}</div>
                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '2px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: '62px', fontWeight: 800, color: '#fff', lineHeight: 1.06, letterSpacing: '-0.04em', marginBottom: '20px' }}>
                  Every item.<br />Every moment.<br />
                  <span style={{ background: 'linear-gradient(90deg,#60a5fa 0%,#a78bfa 45%,#34d399 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>In sync.</span>
                </h1>
                <p style={{ fontSize: '18px', color: '#94a3b8', lineHeight: 1.7, marginBottom: '30px', maxWidth: '420px' }}>
                  Real-time inventory, smart analytics, and team collaboration —{' '}
                  <span style={{ color: '#e2e8f0', fontWeight: 500 }}>built for businesses that can't afford to lose track.</span>
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '28px' }}>
                  {[
                    { icon: '⚡', text: 'Real-time sync', color: '#fbbf24', bg: 'rgba(251,191,36,0.14)', border: 'rgba(251,191,36,0.3)' },
                    { icon: '📊', text: 'Smart analytics', color: '#a78bfa', bg: 'rgba(167,139,250,0.14)', border: 'rgba(167,139,250,0.3)' },
                    { icon: '👥', text: 'Team roles', color: '#34d399', bg: 'rgba(52,211,153,0.14)', border: 'rgba(52,211,153,0.3)' },
                    { icon: '📥', text: 'Excel import', color: '#f87171', bg: 'rgba(248,113,113,0.14)', border: 'rgba(248,113,113,0.3)' },
                    { icon: '🔒', text: 'Free forever', color: '#60a5fa', bg: 'rgba(96,165,250,0.14)', border: 'rgba(96,165,250,0.3)' },
                  ].map(({ icon, text, color, bg, border }) => (
                    <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 16px', background: bg, border: `1px solid ${border}`, borderRadius: '24px' }}>
                      <span style={{ fontSize: '15px' }}>{icon}</span>
                      <span style={{ fontSize: '14px', fontWeight: 600, color }}>{text}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '28px' }}>
                  {[
                    { val: '2.4k+', label: 'Items tracked', color: '#60a5fa', border: 'rgba(96,165,250,0.2)' },
                    { val: '99.9%', label: 'Uptime', color: '#34d399', border: 'rgba(52,211,153,0.2)' },
                    { val: '< 1s', label: 'Sync speed', color: '#a78bfa', border: 'rgba(167,139,250,0.2)' },
                    { val: '100%', label: 'Free', color: '#fbbf24', border: 'rgba(251,191,36,0.2)' },
                  ].map(({ val, label, color, border }) => (
                    <div key={label} style={{ flex: 1, padding: '14px 8px', textAlign: 'center', background: 'rgba(255,255,255,0.05)', border: `1px solid ${border}`, borderRadius: '14px' }}>
                      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: '20px', fontWeight: 700, color, letterSpacing: '-0.02em' }}>{val}</div>
                      <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 500 }}>{label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {[1, 0.5, 0.25].map((o, i) => (
                    <div key={i} style={{ width: `${20 - i * 4}px`, height: '2px', background: '#60a5fa', borderRadius: '2px', opacity: o }} />
                  ))}
                  <span style={{ fontSize: '12px', color: '#475569', letterSpacing: '0.06em' }}>Scroll to explore features ↓</span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* ══ RIGHT / BOTTOM — LOGIN FORM ══ */}
        <div style={{
          width: isMobile ? '100%' : 'min(460px, 38vw)',
          flexShrink: 0,
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'center',
          borderRadius: isMobile ? '24px 24px 0 0' : '0',
          marginTop: isMobile ? '-24px' : '0',
          padding: isMobile ? '32px 20px 0' : '48px 44px',
          paddingBottom: isMobile ? 'max(40px, env(safe-area-inset-bottom))' : '48px',
          backgroundColor: '#ffffff',
          position: 'relative', zIndex: 3,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
          <div style={{ width: '100%', maxWidth: '340px' }}>

            {!showReset ? (
              <>
                <div style={{ marginBottom: '24px' }}>
                  <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? '24px' : '26px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '5px' }}>Welcome back</h2>
                  <p style={{ fontSize: '14px', color: '#64748b' }}>Sign in to your StockSpace account</p>
                </div>

                {/* OAuth error — shown above the OAuth buttons, specific to those */}
                {oauthError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 14px', borderRadius: '12px', fontSize: '14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    ⚠️ {oauthError}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                  <OAuthBtn
                    icon={oauthLoading === 'google' ? '⏳' : '🔵'}
                    label={oauthLoading === 'google' ? 'Signing in…' : 'Google'}
                    onClick={handleGoogle}
                  />
                  <OAuthBtn
                    icon={oauthLoading === 'github' ? '⏳' : '⚫'}
                    label={oauthLoading === 'github' ? 'Signing in…' : 'GitHub'}
                    onClick={handleGithub}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ flex: 1, height: '1px', background: '#f1f5f9' }} />
                  <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 500, whiteSpace: 'nowrap' }}>or continue with email</span>
                  <div style={{ flex: 1, height: '1px', background: '#f1f5f9' }} />
                </div>

                {/* Email error — shown above the email form, specific to it */}
                {emailError && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 14px', borderRadius: '12px', fontSize: '14px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    ⚠️ {emailError}
                  </div>
                )}

                <form onSubmit={handleLogin}>
                  <FloatInput id="email" label="Email address" type="email" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
                  <FloatInput id="password" label="Password" type={showPw ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password"
                    rightSlot={
                      <button type="button" onClick={() => setShowPw(v => !v)}
                        style={{ background: 'none', border: 'none', padding: '4px 0', color: '#3b82f6', fontSize: '13px', fontWeight: 600, cursor: 'pointer', minWidth: '44px', minHeight: '44px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {showPw ? 'Hide' : 'Show'}
                      </button>
                    }
                  />
                  <div style={{ textAlign: 'right', marginTop: '-6px', marginBottom: '18px' }}>
                    <button type="button" onClick={() => { setShowReset(true); setResetEmail(email); }}
                      style={{ background: 'none', border: 'none', padding: '4px 0', color: '#3b82f6', fontSize: '13px', fontWeight: 600, cursor: 'pointer', minHeight: '44px' }}>
                      Forgot password?
                    </button>
                  </div>
                  <button type="submit" disabled={loading || oauthLoading !== ''}
                    onMouseEnter={() => setBtnHover(true)} onMouseLeave={() => setBtnHover(false)}
                    style={primaryBtn(loading || oauthLoading !== '')}>
                    {loading ? 'Signing in…' : 'Sign in →'}
                  </button>
                </form>

                <p style={{ textAlign: 'center', fontSize: '14px', color: '#64748b', marginTop: '20px', paddingBottom: '8px' }}>
                  Don't have an account?{' '}
                  <Link to="/signup" style={{ color: '#3b82f6', fontWeight: 700, textDecoration: 'none' }}>Create one free</Link>
                </p>
              </>
            ) : (
              <>
                {!resetSent ? (
                  <>
                    <button onClick={() => { setShowReset(false); setResetError(''); }}
                      style={{ background: 'none', border: 'none', padding: '4px 0', color: '#64748b', fontSize: '14px', cursor: 'pointer', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '5px', minHeight: '44px' }}>
                      ← Back to sign in
                    </button>
                    <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: '24px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '8px' }}>Reset password</h2>
                    <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '24px', lineHeight: 1.5 }}>Enter your email and we'll send you a reset link.</p>
                    {resetError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px 14px', borderRadius: '12px', fontSize: '14px', marginBottom: '16px' }}>{resetError}</div>}
                    <form onSubmit={handleReset}>
                      <FloatInput id="resetEmail" label="Email address" type="email" value={resetEmail} onChange={e => setResetEmail(e.target.value)} autoComplete="email" />
                      <button type="submit" disabled={resetLoading}
                        onMouseEnter={() => setBtnHover(true)} onMouseLeave={() => setBtnHover(false)}
                        style={primaryBtn(resetLoading)}>
                        {resetLoading ? 'Sending…' : 'Send reset link →'}
                      </button>
                    </form>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', paddingTop: '20px' }}>
                    <div style={{ width: '68px', height: '68px', borderRadius: '50%', background: 'linear-gradient(135deg,#dbeafe,#eff6ff)', border: '2px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px', margin: '0 auto 20px' }}>📧</div>
                    <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: '22px', fontWeight: 700, color: '#0f172a', letterSpacing: '-0.02em', marginBottom: '10px' }}>Check your inbox</h2>
                    <p style={{ fontSize: '14px', color: '#64748b', lineHeight: 1.6, marginBottom: '6px' }}>We've sent a reset link to</p>
                    <p style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b', marginBottom: '20px', wordBreak: 'break-word' }}>{resetEmail}</p>
                    <p style={{ fontSize: '12px', color: '#94a3b8' }}>
                      Didn't get it? Check spam or{' '}
                      <button onClick={() => setResetSent(false)} style={{ background: 'none', border: 'none', color: '#3b82f6', fontSize: '12px', cursor: 'pointer', textDecoration: 'underline', padding: 0, minHeight: '36px' }}>try again</button>.
                    </p>
                    <button onClick={() => { setShowReset(false); setResetSent(false); setResetError(''); }}
                      onMouseEnter={() => setBtnHover(true)} onMouseLeave={() => setBtnHover(false)}
                      style={primaryBtn(false)}>Back to sign in</button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}